import { useId, type CSSProperties } from 'react'

import type { ChartStateValue } from '@/components/charts/ChartState'
import ChartState from '@/components/charts/ChartState'
import SweepClip from '@/components/charts/SweepClip'
import Skeleton from '@/components/ui/Skeleton'
import { cn } from '@/lib/utils'

// The FILL grade, not `--market-up`/`--market-down`: those are tuned for
// text, a shade lighter, and a drawn line is sized like a candle or a bar,
// not a caption (the 2026-09-28 design refresh).
const TONES = {
  up: 'text-market-up-fill',
  down: 'text-market-down-fill',
  flat: 'text-market-flat',
} as const

interface SparklineProps {
  points: readonly number[]
  width?: number
  height?: number
  /** Defaults to last-vs-first. Pass it when the row already knows. */
  direction?: keyof typeof TONES
  /** Figma ships Loading and Failed on this component; both fill the same
   *  `width` x `height` box, so the row never changes height. */
  state?: ChartStateValue
  className?: string
  area?: boolean
  /**
   * Non-uniformly scales the drawn line/area to the rendered box exactly
   * (`preserveAspectRatio="none"`) instead of the default `meet`, which
   * scales `width`/`height` UNIFORMLY and letterboxes the remainder. Every
   * caller so far sizes this component so its box already matches
   * `width`/`height`'s own ratio (fixed, or `h-auto` deriving from it),
   * where `meet` and `none` draw identically. This is for the one shape
   * that doesn't: a FIXED css height beside a FLUID width (a row that
   * stacks below some breakpoint instead). Off by default, so every
   * existing caller's markup is untouched.
   */
  stretch?: boolean
  /**
   * The end dot's colour. `'chart'` (default) is `--chart-marker`, the same
   * gold `IndexChart` ends its own line on, so a trend's last point never
   * doubles as a second up/down signal — see the dot's own comment below.
   * `'foreground'` draws it in the page's text colour instead, for a curve
   * where the dot has to read against its OWN direction fill rather than
   * beside a neutral one. Never a raw white: the token already resolves to
   * the page's near-white in dark mode, and stays legible in light mode too.
   */
  markerColor?: 'chart' | 'foreground'
  /** Off: the line ends where it ends, with no dot (Stock prices' five-day spark, the mock's). */
  endDot?: boolean
  /** The stroke in px. Default 2; the mock's Stock prices spark is 1.5. */
  strokeWidth?: number
}

/**
 * Room for the 2px stroke and the end dot, which would otherwise clip.
 *
 * The dot is `END_DOT_R` (4) plus half the 2px stroke it sits on top of, so 5
 * is the tightest pad that never clips it; rounded up to 6 rather than left at
 * the tight fit.
 */
const PAD = 6

/**
 * Figma's Failed sparkline, in user units off the drawn 120x32: a flat dash
 * with a red dot where the trend's end dot would be. The proportions are the
 * drawn ones — 56 of 120 for the dash, the mark centred — so it still reads
 * as a sparkline that gave up at whatever width the caller asks for.
 *
 * No sentence and no Retry, unlike every other chart here, because Figma
 * draws none: at 120x32 inside a table row there is nowhere to put them, and
 * the retry that matters belongs to the row or the card, not to one cell.
 */
const FAIL_DASH_RATIO = 56 / 120
const FAIL_DASH_H = 2
const FAIL_GAP = 6
const FAIL_DOT_R = 3

/** The end dot's radius — Figma draws it 6px, the 4/8 grid snaps that to 8
 *  (r=4), same as IndexChart's marker (the 2026-09-28 design refresh). */
const END_DOT_R = 4

/**
 * The draw-in: a sweep across the curve over `--motion-sparkline-draw`
 * (`SweepClip`, the same mechanism under `stretch` and without, so a row's
 * curve enters the same whatever its box), and the end dot popping as it
 * lands. CSS alone, on mount alone: a poll that hands over new points changes
 * the path, not the animation, so nothing replays; and a table of these costs
 * no script. Reduced motion collapses both (globals.css).
 */
const DOT_ENTER: CSSProperties = { animationDelay: 'var(--motion-sparkline-draw)' }

/**
 * Hand-rolled SVG rather than a charting library: a watchlist paints five of
 * these per screen and a recharts instance each would cost five ResizeObservers
 * and a re-render per tick, to draw one polyline with no axes and no tooltip.
 *
 * Hidden from assistive tech — the row already prints the change as a signed
 * number, and a shape with no scale adds nothing a screen reader can use.
 */
export default function Sparkline({
  points,
  width = 144,
  height = 16,
  direction,
  state = 'loaded',
  className,
  area = false,
  stretch = false,
  markerColor = 'chart',
  endDot = true,
  strokeWidth = 2,
}: SparklineProps) {
  // A `url(#…)` reference wants a plain name, whatever punctuation useId wraps it in.
  const clipId = `sparkline-${useId().replace(/[^\w-]/g, '')}`

  if (state === 'loading') {
    return (
      <ChartState
        state="loading"
        style={{ width, height }}
        className={cn('inline-block max-w-full align-middle', className)}
        skeleton={<Skeleton className="size-full rounded-sm motion-reduce:animate-none" />}
      />
    )
  }

  if (state === 'failed') {
    const dashW = width * FAIL_DASH_RATIO
    const markX = (width - (dashW + FAIL_GAP + FAIL_DOT_R * 2)) / 2
    const midY = height / 2

    return (
      <span
        className={cn('inline-block max-w-full align-middle', className)}
        style={{ width, height }}
      >
        <svg
          width={width}
          height={height}
          viewBox={`0 0 ${String(width)} ${String(height)}`}
          className="block max-w-full text-border-strong"
          aria-hidden="true"
          focusable="false"
        >
          <rect
            x={markX}
            y={midY - FAIL_DASH_H / 2}
            width={dashW}
            height={FAIL_DASH_H}
            rx={FAIL_DASH_H / 2}
            fill="currentColor"
          />
          <circle
            cx={markX + dashW + FAIL_GAP + FAIL_DOT_R}
            cy={midY}
            r={FAIL_DOT_R}
            className="fill-destructive-fill"
          />
        </svg>
        {/* The mark above is aria-hidden and says nothing on its own, and the
            row's price is still on screen — so without this the failure is
            invisible to a screen reader. Polite: a 52-week table of twenty
            failed sparklines would otherwise fire twenty alerts. */}
        <span role="status" className="sr-only">
          Couldn't load the chart
        </span>
      </span>
    )
  }

  // A line needs two points. One point is not a trend — drawn, it is a lone
  // end-dot that reads as a rendering bug (the Market overview's index cards
  // showed exactly that while they were fed `[value]`), so draw nothing.
  if (points.length < 2) return null
  const first = points[0]
  const last = points[points.length - 1]
  if (first === undefined || last === undefined) return null

  const tone = direction ?? (last > first ? 'up' : last < first ? 'down' : 'flat')

  const min = Math.min(...points)
  const max = Math.max(...points)
  // A flat series has no range to normalise against; centre it instead of
  // dividing by zero and pushing every point to the top of the box.
  const span = max - min || 1

  const stepX = (width - PAD * 2) / (points.length - 1)
  const x = (index: number) => PAD + index * stepX
  const y = (value: number) => PAD + (1 - (value - min) / span) * (height - PAD * 2)

  const path = points
    .map((value, i) => `${i === 0 ? 'M' : 'L'}${x(i).toFixed(2)} ${y(value).toFixed(2)}`)
    .join(' ')

  const markerFill = markerColor === 'foreground' ? 'fill-foreground' : 'fill-chart-marker'
  const markerStroke = markerColor === 'foreground' ? 'stroke-foreground' : 'stroke-chart-marker'
  const lastX = x(points.length - 1)

  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${String(width)} ${String(height)}`}
      preserveAspectRatio={stretch ? 'none' : undefined}
      className={cn('max-w-full', TONES[tone], className)}
      aria-hidden="true"
      focusable="false"
    >
      <defs>
        <SweepClip id={clipId} width={width} height={height} pace="sparkline" />
      </defs>
      <g clipPath={`url(#${clipId})`}>
        {area ? (
          <path
            d={`${path} L${lastX.toFixed(2)} ${String(height)} L${x(0).toFixed(2)} ${String(height)} Z`}
            fill="currentColor"
            opacity={0.12}
          />
        ) : null}
        <path
          d={path}
          fill="none"
          stroke="currentColor"
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeLinejoin="round"
          vectorEffect={stretch ? 'non-scaling-stroke' : undefined}
        />
      </g>
      {/* `--chart-marker` (gold) by default, never `currentColor`/the
          direction tone — the same mark IndexChart ends its line on, so a
          trend's last point never doubles as a second up/down signal.
          `stretch`'s non-uniform scale would draw a plain `r`-radius circle
          as an ellipse (x and y no longer share one scale factor), so that
          mode draws the dot as a zero-length, round-capped STROKE instead:
          `non-scaling-stroke` renders a stroke's width in the outermost,
          unscaled coordinate system, so the cap stays a true circle at a
          constant pixel size no matter how far the box stretches. */}
      {!endDot ? null : stretch ? (
        <path
          d={`M${lastX.toFixed(2)} ${y(last).toFixed(2)} L${lastX.toFixed(2)} ${y(last).toFixed(2)}`}
          strokeWidth={END_DOT_R * 2}
          strokeLinecap="round"
          vectorEffect="non-scaling-stroke"
          className={cn(markerStroke, 'origin-center animate-pop transform-fill')}
          style={DOT_ENTER}
        />
      ) : (
        <circle
          cx={lastX}
          cy={y(last)}
          r={END_DOT_R}
          className={cn(markerFill, 'origin-center animate-pop transform-fill')}
          style={DOT_ENTER}
        />
      )}
    </svg>
  )
}
